import api from '../../utils/api';
import { alertError, alertSuccess } from './alert';
import { retailers } from '../../constants/filters';
import { systemProperties } from './systemProperties';
import { downloadDocument } from '../../utils/exportHelper';
import { getOrderDocuments, getOrderDocumentsFilename } from '../../utils/orderHelper';
import {
  BRAND_ORDERS_COUNT,
  BRAND_ORDERS_OFFSET,
  BRAND_ORDERS_PER_PAGE,
  BRAND_ORDERS_ORDER,
  BRAND_ORDERS_FILTERS,
  BRAND_ORDERS_REPORT_MODAL,
  BRAND_ORDERS_FILTER_DATA,
  BRAND_ORDERS_LOADING,
  BRAND_ORDERS_ORDER_LOADING,
  BRAND_ORDERS_LOADING_FILTERS,
  BRAND_ORDERS_SEARCH,
  BRAND_ORDERS_SORT,
  BRAND_ORDERS_STATUS_COUNT,
  BRAND_ORDERS_DOWNLOAD_DOCUMENTS,
} from '../constants/brandOrdersTypes';

import { SYSTEM_PROPERTIES_TYPE_RETURN_REASON } from '../constants/systemPropertiesTypes';
import { getMomentUtcDateRangeFromFormat } from '../../utils/dateHelper';
import { DATE_FORMAT } from '../../constants/dateFormats';
import { filterOrdersByStates } from '../../utils/orderTableHelper';
import { getTotalCountHeader } from './api/utils';
import _ from 'lodash';

export const setBrandOrdersLoading = (value) => ({ type: BRAND_ORDERS_LOADING, payload: value });
export const setBrandOrdersReport = (value) => ({ type: BRAND_ORDERS_REPORT_MODAL, payload: value });
export const setBrandOrdersLoadingFilters = (value) => ({ type: BRAND_ORDERS_LOADING_FILTERS, payload: value });

const setBrandOrdersFilters = (filters) => ({ type: BRAND_ORDERS_FILTERS, payload: filters });
const setBrandOrdersCount = (count) => ({ type: BRAND_ORDERS_COUNT, payload: { all: count } });
const setBrandOrdersFilterData = (payload) => ({ type: BRAND_ORDERS_FILTER_DATA, payload });
const setBrandOrdersStatusCount = (count) => ({ type: BRAND_ORDERS_STATUS_COUNT, payload: count });

export const postBrandOrdersReturns = (data, retailer_order_id, id) => async (dispatch, getState) => {
  const { supplierId } = getState().role;

  try {
    const response = await postReturns(supplierId, id, data);

    if (response.data.success) {
      dispatch(alertSuccess(response.data.message));
      dispatch(getBrandOrders());
    } else {
      dispatch(alertError('Error'));
    }
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const setBrandOrdersOffset = (offset) => (dispatch) => {
  dispatch({ type: BRAND_ORDERS_OFFSET, payload: offset });
  dispatch(getBrandOrders());
};

export const setBrandOrdersPerPage = (perPage) => (dispatch) => {
  dispatch({ type: BRAND_ORDERS_PER_PAGE, payload: perPage });
  dispatch({ type: BRAND_ORDERS_OFFSET, payload: 0 });
  dispatch(getBrandOrders());
};

export const setBrandOrdersSearchById = (search) => (dispatch) => {
  dispatch({ type: BRAND_ORDERS_SEARCH, payload: search });
  dispatch(getBrandOrders());
};

export const setBrandOrdersSort = (order) => (dispatch) => {
  dispatch({ type: BRAND_ORDERS_SORT, payload: order });
  dispatch(getBrandOrders());
};

export const getBrandOrders = (orderStatus) => async (dispatch, getState) => {
  try {
    dispatch(setBrandOrdersLoading(true));

    if (orderStatus) {
      dispatch({ type: BRAND_ORDERS_OFFSET, payload: 0 });
    }

    const state = getState();
    const { retailer } = state.pageFilters.orders;
    const { supplierId } = state.role;
    const { startDate, endDate } = state.pageFilters.dateRange;
    const { activeCount, offset, perPage, search, sort, field } = state.brandOrders;
    const { returnReasons } = state.systemProperties;

    if (!returnReasons || !returnReasons.length) {
      dispatch(systemProperties(SYSTEM_PROPERTIES_TYPE_RETURN_REASON, 'returnReasons'));
    }
    const { startDateResult, endDateResult } = getMomentUtcDateRangeFromFormat(startDate, endDate, DATE_FORMAT);

    const filters = {
      created_at: {
        $between: [startDateResult, endDateResult],
      },
    };

    const orderStatusLower = orderStatus ? orderStatus.toLowerCase() : activeCount.toLowerCase();
    filterOrdersByStates(filters, orderStatusLower);

    if (search && search !== '') {
      Object.assign(filters, {
        $or: [
          {
            retailer_order_id: {
              $like: `%${search}%`,
            },
          },
          {
            sales_order_number: {
              $like: `%${search}%`,
            },
          },
          {
            fulfillment_id: {
              $like: `%${search}%`,
            },
          },
          {
            order_id: {
              $like: `%${search}%`,
            },
          },
        ],
      });
    }

    if (retailer && retailer !== 'all') {
      filters.retailer_id = retailer;
    }

    const response = await orders(supplierId, filters, perPage, offset, sort, field);

    dispatch(setBrandOrdersFilterData({ data: response.data, title: orderStatus ? orderStatus : activeCount }));
    dispatch(setBrandOrdersStatusCount(getTotalCountHeader(response)));
    dispatch(setBrandOrdersLoading(false));
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const getBrandOrder = (id) => async (dispatch, getState) => {
  dispatch({ type: BRAND_ORDERS_ORDER_LOADING, payload: true });

  try {
    const { supplierId } = getState().role;
    const response = await order(supplierId, id);

    dispatch({ type: BRAND_ORDERS_ORDER, payload: response.data });
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const getBrandOrdersFilters = (changeRetailers) => async (dispatch, getState) => {
  dispatch(setBrandOrdersLoadingFilters(true));

  try {
    const state = getState();
    const { supplierId } = state.role;
    const { retailer } = state.pageFilters.orders;
    const { startDate, endDate } = state.pageFilters.dateRange;

    const filters = {
      created_at: {
        $between: [startDate, endDate],
      },
    };

    if (retailer && retailer !== 'all') {
      filters.retailer_id = retailer;
    }

    const response = await orderFilters(supplierId, filters);
    const data = response.data;

    if (changeRetailers && retailer === 'all') {
      const newRetailers = data.orders_per_retailer.map((retailer) => ({
        label: retailer.retailer_name,
        value: retailer.retailer_id,
      }));

      data.orders_per_retailer = [...retailers, ...newRetailers];
    } else {
      delete data.orders_per_retailer;
    }

    const sum = Object.values(_.omit(data.orders_per_status, 'types')).reduce((a, b) => a + b, 0);

    dispatch(setBrandOrdersFilters(data));
    dispatch(setBrandOrdersCount(sum));
    dispatch(setBrandOrdersLoadingFilters(false));
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const getBrandOrdersAndFilters =
  (changeRetailers = false) =>
  async (dispatch, getState) => {
    const state = getState();
    if (state.brandOrders.isLoading) {
      return;
    }

    dispatch(setBrandOrdersLoading(true));
    dispatch(setBrandOrdersLoadingFilters(true));

    const { retailer } = state.pageFilters.orders;
    const { supplierId } = state.role;
    const { startDate, endDate } = state.pageFilters.dateRange;
    const { activeCount, offset, perPage, search, sort, field } = state.brandOrders;
    const { returnReasons } = state.systemProperties;
    const { startDateResult, endDateResult } = getMomentUtcDateRangeFromFormat(startDate, endDate, DATE_FORMAT);

    const ordersFilters = {
      created_at: {
        $between: [startDateResult, endDateResult],
      },
    };

    const ordersFiltersFilters = {
      created_at: {
        $between: [startDateResult, endDateResult],
      },
    };

    const orderStatus = activeCount.toLowerCase();
    filterOrdersByStates(ordersFilters, orderStatus);

    if (search && search !== '') {
      Object.assign(ordersFilters, {
        $or: [
          {
            retailer_order_id: {
              $like: `%${search}%`,
            },
          },
          {
            sales_order_number: {
              $like: `%${search}%`,
            },
          },
          {
            fulfillment_id: {
              $like: `%${search}%`,
            },
          },
          {
            order_id: {
              $like: `%${search}%`,
            },
          },
        ],
      });
    }

    if (retailer && retailer !== 'all') {
      ordersFilters.retailer_id = retailer;
      ordersFiltersFilters.retailer_id = retailer;
    }

    try {
      if (!returnReasons || !returnReasons.length) {
        dispatch(systemProperties(SYSTEM_PROPERTIES_TYPE_RETURN_REASON, 'returnReasons'));
      }

      const ordersPromise = orders(supplierId, ordersFilters, perPage, offset, sort, field);
      const ordersFiltersPromise = orderFilters(supplierId, ordersFiltersFilters);
      const [ordersResponse, ordersFiltersResponse] = await Promise.all([ordersPromise, ordersFiltersPromise]);

      dispatch(setBrandOrdersFilterData({ data: ordersResponse.data, title: activeCount }));
      dispatch(setBrandOrdersStatusCount(getTotalCountHeader(ordersResponse)));

      const filtersData = ordersFiltersResponse.data;
      if (changeRetailers && retailer === 'all') {
        const newRetailers = filtersData.orders_per_retailer.map((retailer) => ({
          label: retailer.retailer_name,
          value: retailer.retailer_id,
        }));

        filtersData.orders_per_retailer = [...retailers, ...newRetailers];
      } else {
        delete filtersData.orders_per_retailer;
      }

      const sum = Object.values(_.omit(filtersData.orders_per_status, 'types')).reduce((a, b) => a + b, 0);

      dispatch(setBrandOrdersFilters(filtersData));
      dispatch(setBrandOrdersCount(sum));

      dispatch(setBrandOrdersLoading(false));
      dispatch(setBrandOrdersLoadingFilters(false));
    } catch (error) {
      dispatch(alertError(error));
    }
  };

export const ordersReport =
  (useOriginalCurrency = true) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const { supplierId } = state.role;
      const { startDate, endDate } = state.pageFilters.dateRange;

      const data = {
        type: 'orders',
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
        use_original_currency: useOriginalCurrency,
      };

      const response = await downloadReports(supplierId, data);

      if (response.data.success) {
        dispatch(setBrandOrdersReport(true));
      }
    } catch (error) {
      dispatch(alertError(error));
    }
  };

export const cancelFulfillments = (ids) => async (dispatch, getState) => {
  const { supplierId } = getState().role;
  const status = 'out_of_stock';

  try {
    for (let i = 0; i < ids.length; i++) {
      const fulfillmentId = ids[i];
      const response = await api.put(`/suppliers/${supplierId}/fulfillments/${fulfillmentId}/setStatus/${status}`, {});

      if (response.data && response.data.success) {
        dispatch(alertSuccess(`Cancel fulfillment #${fulfillmentId} (set status to ${status})`));
      } else {
        dispatch(alertError(`Error canceling fulfillment #${fulfillmentId} (set status to ${status})`));
      }
    }
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const fulfillOrder = (data, identifiers) => async (dispatch, getState) => {
  const { supplierId } = getState().role;

  try {
    const { shipmentsData, rejectionsData } = calculateFulfillData(data, identifiers);

    if (shipmentsData.length) {
      const response = await postFulfillmentShipments(supplierId, shipmentsData);
      if (!response.data || !response.data.success) {
        dispatch(alertError(`Error fulfilling order`));
        return;
      }
    }

    if (rejectionsData.length) {
      const response = await postFulfillmentRejections(supplierId, rejectionsData);
      if (!response.data || !response.data.success) {
        dispatch(alertError(`Error fulfilling order`));
        return;
      }
    }

    dispatch(alertSuccess(`Successfully fulfilled order`));
  } catch (error) {
    dispatch(alertError(error));
  }
};

export const brandOrdersDownloadDocuments = (orders) => async (dispatch, getState) => {
  const { supplierId } = getState().role;

  dispatch({ type: BRAND_ORDERS_DOWNLOAD_DOCUMENTS, payload: orders });

  try {
    if (orders.length === 1) {
      const firstOrder = orders[0];
      const documents = getOrderDocuments(firstOrder);
      const documentLinks = Object.values(documents).flat();
      if (documentLinks.length === 1) {
        const filename = getOrderDocumentsFilename(firstOrder);
        downloadDocument(documentLinks[0], filename);
        dispatch(alertSuccess(`Downloading documents Order ${firstOrder.retailer_order_id}`));
        return;
      }
    }

    const orderIds = orders.map((order) => order.id);
    const response = await api.post(`/suppliers/${supplierId}/orders/download-documents`, {
      order_ids: orderIds,
      format: 'pdf',
    });
    if (response.data?.success) {
      dispatch(alertSuccess(response.data.message ?? `Downloading documents of Order ${orders.length} orders`));
    } else {
      dispatch(alertError({ message: 'Error downloading documents' }));
    }
  } catch (error) {
    dispatch(alertError(error));
  } finally {
    dispatch({ type: BRAND_ORDERS_DOWNLOAD_DOCUMENTS, payload: null });
  }
};

const postReturns = (supplierId, fulfillmentId, data) => {
  return api.post(`/suppliers/${supplierId}/fulfillments/${fulfillmentId}/returns`, data);
};

let cancelOrders;
const orders = (supplierId, filters, perPage, offset, sort, field) => {
  if (cancelOrders) {
    cancelOrders('Cancel brand orders');
  }

  const params = new URLSearchParams();
  params.append('filter', JSON.stringify(filters));
  params.append('limit', perPage);
  params.append('offset', offset);
  params.append('order', sort);
  params.append('sort', field);
  params.append('full', false);

  return api.get(`/suppliers/${supplierId}/orders?` + params.toString(), {
    cancelToken: new api.CancelToken((c) => (cancelOrders = c)),
  });
};

let cancelOrderFilters;
const orderFilters = (supplierId, filters) => {
  if (cancelOrderFilters) {
    cancelOrderFilters('Cancel brand orders filters');
  }

  return api.get(`/suppliers/${supplierId}/order-filters?filter=${JSON.stringify(filters)}`, {
    cancelToken: new api.CancelToken((c) => (cancelOrderFilters = c)),
  });
};

let cancelOrder;
const order = (supplier_id, id) => {
  if (cancelOrder) {
    cancelOrder('Cancel brand order');
  }

  return api.get(`/suppliers/${supplier_id}/orders/${id}`, {
    cancelToken: new api.CancelToken((c) => (cancelOrder = c)),
  });
};

let cancelReports;
const downloadReports = (supplier_id, data) => {
  if (cancelReports) {
    cancelReports('Cancel brands orders report');
  }

  return api.post(`/suppliers/${supplier_id}/reports`, data, {
    cancelToken: new api.CancelToken((c) => (cancelReports = c)),
  });
};

let cancelFulfillmentShipments;
const postFulfillmentShipments = (supplier_id, data) => {
  if (cancelFulfillmentShipments) {
    cancelFulfillmentShipments('Cancel post Fulfillment Shipments');
  }

  return api.post(`/suppliers/${supplier_id}/fulfillments/shipments`, data, {
    cancelToken: new api.CancelToken((c) => (cancelFulfillmentShipments = c)),
  });
};

let cancelFulfillmentRejections;
const postFulfillmentRejections = (supplier_id, data) => {
  if (cancelFulfillmentRejections) {
    cancelFulfillmentRejections('Cancel post Fulfillment Rejections');
  }

  return api.post(`/suppliers/${supplier_id}/fulfillments/rejections`, data, {
    cancelToken: new api.CancelToken((c) => (cancelFulfillmentRejections = c)),
  });
};

function calculateFulfillData(data, identifiers) {
  const shipmentsData = [];
  const rejectionsData = [];

  Object.keys(data).forEach((fulfillmentId) => {
    const fulfillmentData = data[fulfillmentId];
    if (fulfillmentData.shipments.length) {
      const fulfillmentShipments = [];
      fulfillmentData.shipments.forEach((shipment) => {
        const items = Object.keys(shipment.quantities)
          .map((fulfillmentLineId) => {
            const quantity = shipment.quantities[fulfillmentLineId];
            if (quantity > 0) {
              const { type, id } = identifiers[fulfillmentLineId];
              return {
                [type]: id,
                quantity,
              };
            }
            return false;
          })
          .filter(Boolean);

        if (items.length) {
          fulfillmentShipments.push({
            trackingNumber: shipment.trackingNumber,
            shippingMethodCode: shipment.shippingMethod,
            items,
          });
        }
      });

      if (fulfillmentShipments.length) {
        shipmentsData.push({
          id: parseInt(fulfillmentId),
          shipments: fulfillmentShipments,
        });
      }
    }

    if (fulfillmentData.rejection) {
      const { quantities, rejectId } = fulfillmentData.rejection;
      const items = Object.keys(quantities)
        .map((fulfillmentLineId) => {
          const quantity = quantities[fulfillmentLineId];
          if (quantity > 0) {
            const { type, id } = identifiers[fulfillmentLineId];
            return {
              [type]: id,
              quantity: quantities[fulfillmentLineId],
            };
          }
          return false;
        })
        .filter(Boolean);

      if (items.length) {
        rejectionsData.push({
          id: parseInt(fulfillmentId),
          rejectId: rejectId,
          items,
        });
      }
    }
  });

  return { shipmentsData, rejectionsData };
}
